import {
  gspx,
  GPX_EVENT_TYPES,
  GPX_FEATURES,
  GPX_CAPABILITIES,
  GPX_PARTNERS,
  GPX_ROUTES,
} from '@rsos/gainsight';
import { hashTrackingId } from './hashTrackingId';

// Event names specific to Air Ambulance (currently GMR)
const AIR_AMBULANCE_EVENT_NAMES = {
  REQUEST_AIR_AMBULANCE: 'request_air_ambulance',
  OPEN_AIR_AMBULANCE_REQUEST_FORM: 'open_air_ambulance_request_form',
  CLOSE_AIR_AMBULANCE_REQUEST_FORM: 'close_air_ambulance_request_form',
  SUBMIT_AIR_AMBULANCE_REQUEST: 'submit_air_ambulance_request',
  SUBMIT_AIR_AMBULANCE_REQUEST_API_RESPONSE:
    'submit_air_ambulance_request_api_response',
  EDIT_AIR_AMBULANCE_REQUEST: 'edit_air_ambulance_request',
  CANCEL_AIR_AMBULANCE_REQUEST: 'cancel_air_ambulance_request',
  CANCEL_AIR_AMBULANCE_REQUEST_API_RESPONSE:
    'cancel_air_ambulance_request_api_response',
  OPEN_TRANSPORT_LINK: 'open_transport_link',
  COPY_TRACKING_LINK: 'copy_tracking_link',
  LOCATION_SELECTION: 'landing_location',
  SHOW_STATUS_WINDOW: 'show_air_ambulance_status',
  DISPLAY_STATUS_UPDATE: 'display_status_update',
  AIR_AMBULANCE_LAYER_CLICK_SHOW: 'show_air_ambulance_layer',
  AIR_AMBULANCE_LAYER_CLICK_HIDE: 'hide_air_ambulance_layer',
  // TODO: Not yet implemented
  LANDING_ZONE_CLICK: 'landing_zone_entity',
  LOCATION_CHANGE: 'change_location',
  FAQ_CLICK: 'open_faq_link',
  TOOLTIP_CLICK: 'open_form_help_tooltip',
} as const satisfies Record<string, string>;

// Source surface that opens the air ambulance request form
export const AIR_AMBULANCE_FORM_SOURCE = {
  LANDING_ZONE_MAP_MARKER: 'landing_zone_map_marker',
} as const;

export type AirAmbulanceFormSource =
  (typeof AIR_AMBULANCE_FORM_SOURCE)[keyof typeof AIR_AMBULANCE_FORM_SOURCE];

// Source button that opens the air ambulance request form
export const AIR_AMBULANCE_SOURCE_BUTTON = {
  ACTION_BAR: 'action_bar',
  OVERVIEW_TAB: 'overview_tab',
  TOOLS_TAB: 'tools_tab',
  RESOURCE_PANEL_OPTION: 'resource_panel_option',
} as const;

export type AirAmbulanceSourceButton =
  (typeof AIR_AMBULANCE_SOURCE_BUTTON)[keyof typeof AIR_AMBULANCE_SOURCE_BUTTON];

export type LocationSelectionMethod =
  | 'map_click'
  | 'address_input'
  | 'coordinates_input'
  | 'call_location'
  | 'existing_request';

interface TrackRequestAirAmbulanceClickParams {
  /** The source button that opened the air ambulance request form */
  sourceButton: AirAmbulanceSourceButton;
}

/**
 * Tracks when a user clicks the "Request Air Ambulance" button in ACTION BAR.
 */
export const trackRequestAirAmbulanceClick = ({
  sourceButton,
}: TrackRequestAirAmbulanceClickParams): void => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.REQUEST_AIR_AMBULANCE,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    additionalKeys: {
      source_button: sourceButton,
    },
  });
};

interface TrackOpenAirAmbulanceRequestFormParams {
  /** The UUID of the form open event. Optional when the form is opened from a map marker before a UUID is generated. */
  uuid?: string;
  /** The source that opened the form */
  source?: AirAmbulanceFormSource;
  /** The name of the landing zone, when opened from a map marker */
  landingZoneName?: string;
  /** The ID of the landing zone, when opened from a map marker */
  landingZoneId?: string;
}

/**
 * Tracks when a user opens the air ambulance request form.
 */
export const trackOpenAirAmbulanceRequestForm = ({
  uuid,
  source,
  landingZoneName,
  landingZoneId,
}: TrackOpenAirAmbulanceRequestFormParams): void => {
  const entities = uuid !== undefined ? { uuid } : undefined;

  const additionalKeys =
    source !== undefined
      ? {
          source,
          ...(landingZoneName !== undefined && {
            landing_zone_name: landingZoneName,
          }),
          ...(landingZoneId !== undefined && {
            landing_zone_id: landingZoneId,
          }),
        }
      : undefined;

  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.OPEN_AIR_AMBULANCE_REQUEST_FORM,
    type: GPX_EVENT_TYPES.INFO,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities,
    additionalKeys,
  });
};

/**
 * Tracks when a user closes the air ambulance request form.
 */
export const trackCloseAirAmbulanceRequestForm = (): void => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.CLOSE_AIR_AMBULANCE_REQUEST_FORM,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
  });
};

interface TrackSubmitAirAmbulanceRequestParams {
  /** The UUID of the form open event */
  uuid: string;
  /** The ID of the request being submitted */
  requestId: string | null | undefined;
  /** The type of the request (e.g., immediate, standby) */
  requestType: string;
  /** Whether this is an edit of an existing request (true) or a new submission (false) */
  isEdit?: boolean;
}

/**
 * Tracks when a user submits an air ambulance request.
 */
export const trackSubmitAirAmbulanceRequest = ({
  uuid,
  requestId,
  requestType,
  isEdit = false,
}: TrackSubmitAirAmbulanceRequestParams): void => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.SUBMIT_AIR_AMBULANCE_REQUEST,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    additionalKeys: {
      is_edit: isEdit,
    },
    entities: {
      request_id: hashTrackingId(requestId),
      request_type: requestType,
      uuid,
    },
  });
};

interface TrackEditAirAmbulanceRequestParams {
  /** The ID of the request being edited */
  requestId: string | null | undefined;
}

/**
 * Tracks when a user clicks the edit button for an existing air ambulance request.
 */
export const trackEditAirAmbulanceRequest = ({
  requestId,
}: TrackEditAirAmbulanceRequestParams): void => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.EDIT_AIR_AMBULANCE_REQUEST,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashTrackingId(requestId),
    },
  });
};

interface TrackCancelAirAmbulanceRequestParams {
  /** The ID of the request being cancelled */
  requestId: string | null | undefined;
  /** The reason for the cancellation */
  reason: string;
}

/**
 * Tracks when a user cancels an air ambulance request.
 */
export const trackCancelAirAmbulanceRequest = ({
  requestId,
  reason,
}: TrackCancelAirAmbulanceRequestParams): void => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.CANCEL_AIR_AMBULANCE_REQUEST,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    additionalKeys: {
      reason,
    },
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashTrackingId(requestId),
    },
  });
};

interface TrackOpenTransportLinkParams {
  /** The URL of the Transport.net link */
  airAmbulanceUrl: string | undefined;
  /** The requestId associated with the request */
  requestId: string | null | undefined;
}

/**
 * Tracks when a user clicks a Transport.net link.
 */
export const trackOpenTransportLink = ({
  airAmbulanceUrl,
  requestId,
}: TrackOpenTransportLinkParams): void => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.OPEN_TRANSPORT_LINK,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashTrackingId(requestId),
      air_ambulance_url: airAmbulanceUrl,
    },
  });
};

interface TrackLocationSelectionParams {
  /** The requestId for the selected location */
  requestId: string | null | undefined;
  /** The location of the associated request */
  location: { latitude: number; longitude: number } | null;
  /** How the location was selected */
  selectionMethod?: LocationSelectionMethod;
  /** The form UUID to link with form open events */
  uuid?: string;
}

/**
 * Tracks when a user selects a location.
 */
export const trackLocationSelection = ({
  requestId,
  location,
  selectionMethod,
  uuid,
}: TrackLocationSelectionParams): void => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.LOCATION_SELECTION,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    additionalKeys: selectionMethod
      ? { selection_method: selectionMethod }
      : undefined,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashTrackingId(requestId),
      location,
      ...(uuid && { uuid }),
    },
  });
};

interface TrackCopyTrackingLinkParams {
  /** The requestId associated with the request */
  requestId: string | null | undefined;
  /** The URL of the tracking link */
  airAmbulanceUrl: string | undefined;
}

/**
 * Tracks when a user copies a tracking link.
 */
export const trackCopyTrackingLink = ({
  requestId,
  airAmbulanceUrl,
}: TrackCopyTrackingLinkParams): void => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.COPY_TRACKING_LINK,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashTrackingId(requestId),
      air_ambulance_url: airAmbulanceUrl,
    },
  });
};

interface TrackAirAmbulanceLayerToggleParams {
  /** The ID of the layer */
  layerId: string;
  /** Whether the layer is being shown (true) or hidden (false) */
  isVisible: boolean;
}

/**
 * Tracks when a user toggles the Air Ambulance layer visibility.
 */
export const trackAirAmbulanceLayerToggle = ({
  layerId,
  isVisible,
}: TrackAirAmbulanceLayerToggleParams): void => {
  gspx.trackEvent({
    name: isVisible
      ? AIR_AMBULANCE_EVENT_NAMES.AIR_AMBULANCE_LAYER_CLICK_SHOW
      : AIR_AMBULANCE_EVENT_NAMES.AIR_AMBULANCE_LAYER_CLICK_HIDE,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      layer_id: layerId,
    },
  });
};

interface TrackDisplayStatusUpdateParams {
  /** The ID of the associated request */
  requestId: string | null | undefined;
  /** The current status of the request */
  status: string;
}

/**
 * Tracks when a status update is displayed.
 */
export const trackDisplayStatusUpdate = ({
  requestId,
  status,
}: TrackDisplayStatusUpdateParams): void => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.DISPLAY_STATUS_UPDATE,
    type: GPX_EVENT_TYPES.API_PUSH,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    additionalKeys: {
      status,
    },
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashTrackingId(requestId),
    },
  });
};

interface TrackSubmitAirAmbulanceRequestApiResponseParams {
  /** The ID of the request being submitted */
  requestId: string | null | undefined;
  /** The response status from the API */
  status: number | undefined;
  /** The response error from the API */
  error: string | boolean;
  /** The response success from the API */
  success: string | boolean;
  /** The UUID of the form submission event */
  UUID: string;
  /** Whether this is an edit of an existing request (true) or a new submission (false) */
  isEdit?: boolean;
}

/**
 * Tracks response from the API when a user submits an air ambulance request.
 */
export const trackSubmitAirAmbulanceRequestApiResponse = ({
  requestId,
  status,
  error,
  success,
  UUID,
  isEdit = false,
}: TrackSubmitAirAmbulanceRequestApiResponseParams): void => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.SUBMIT_AIR_AMBULANCE_REQUEST_API_RESPONSE,
    type: GPX_EVENT_TYPES.API_RESPONSE,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    additionalKeys: {
      status,
      error,
      success,
      is_edit: isEdit,
    },
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashTrackingId(requestId),
      uuid: UUID,
    },
  });
};

interface TrackCancelAirAmbulanceRequestApiResponseParams {
  /** The ID of the request being cancelled */
  requestId: string | null | undefined;
  /** The response status from the API */
  status: number | undefined;
  /** The response error from the API */
  error: string | boolean;
  /** The response success from the API */
  success: string | boolean;
  UUID: string;
}

/**
 * Tracks response from the API when a user cancels an air ambulance request.
 */
export const trackCancelAirAmbulanceRequestApiResponse = ({
  requestId,
  status,
  error,
  success,
  UUID,
}: TrackCancelAirAmbulanceRequestApiResponseParams): void => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.CANCEL_AIR_AMBULANCE_REQUEST_API_RESPONSE,
    type: GPX_EVENT_TYPES.API_RESPONSE,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    additionalKeys: {
      status,
      error,
      success,
    },
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashTrackingId(requestId),
      uuid: UUID,
    },
  });
};

interface TrackStatusPageRenderParams {
  /** The ID of the associated request */
  requestId: string | null | undefined;
  /** The current state of the status page (e.g., SUBMITTED, ACCEPTED, CANCELLED) */
  status: string;
}

/**
 * Tracks when the AirAmbulanceStatus component is rendered.
 */
export const trackStatusPageRender = ({
  requestId,
  status,
}: TrackStatusPageRenderParams): void => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.SHOW_STATUS_WINDOW,
    type: GPX_EVENT_TYPES.INFO,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    additionalKeys: {
      status,
    },
    entities: {
      request_id: hashTrackingId(requestId),
    },
  });
};

interface TrackLandingZoneClickParams {
  /** The ID of the landing zone clicked */
  zoneId: string;
  /** The requestId for the selected location */
  requestId: string | null | undefined;
}

// TODO: Not implemented yet
/**
 * Tracks when a user clicks on a landing zone.
 */
export const trackLandingZoneClick = ({
  zoneId,
  requestId,
}: TrackLandingZoneClickParams): void => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.LANDING_ZONE_CLICK,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashTrackingId(requestId),
      zone_id: zoneId,
    },
  });
};

interface TrackLocationChangeParams {
  /** The ID of the associated request */
  requestId: string | null | undefined;
  /** The location of the associated request */
  location: { latitude: number; longitude: number } | null;
}

/**
 * Tracks when a user changes the location.
 */
export const trackLocationChange = ({
  requestId,
  location,
}: TrackLocationChangeParams): void => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.LOCATION_CHANGE,
    type: GPX_EVENT_TYPES.INFO,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashTrackingId(requestId),
      location,
    },
  });
};

interface TrackFAQClickParams {
  /** The requestId associated with the request */
  requestId: string | null | undefined;
}

/**
 * Tracks when a user clicks on an FAQ link.
 */
export const trackFAQClick = ({ requestId }: TrackFAQClickParams): void => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.FAQ_CLICK,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashTrackingId(requestId),
    },
  });
};

interface TrackTooltipClickParams {
  /** The requestId associated with the request */
  requestId: string | null | undefined;
}

/**
 * Tracks when a user opens a help tooltip.
 */
export const trackTooltipClick = ({
  requestId,
}: TrackTooltipClickParams): void => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.TOOLTIP_CLICK,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashTrackingId(requestId),
    },
  });
};
